/**
 * The recon drone (WORKBOARD E6, §10.3): layer 0's flown probe. A hand-launched fixed-wing —
 * a high wing on a pylon, a pusher prop, a camera turret under the nose — its wingspan just over
 * the sim's hit sphere (DRONE_HIT_RADIUS: 0.8 u across at classic, under a person), so what you see
 * is what a bullet must hit; small, as the owner ruled; markers carry it at range. One hue in three values (unitPalette.ts):
 * the fuselage is the team body, the wings are the dark (they are what the ground sees against
 * the sky), the wingtips and the lens are the flash.
 *
 * Built in person units (a person is 1.8 tall) and scaled by `personHeight / 1.8`. Faces +Z,
 * wings along X. Presentation only: the host sets the bank, pitch and prop speed from the
 * flight; nothing here reads or writes game state.
 *
 * 65.16 (models only; lane B builds the sim): the **strike drone**, the owner's kamikaze variant — the same airframe
 * with one HE charge slung under its belly on a release clip. The charge is the HE grenade (rendering/fieldKit.ts) at
 * its own size, armed (its pin out), so the two read as the same munition. Its dive is an attitude (`STRIKE_DIVE`):
 * nose down steeply, the prop at full.
 *
 * 67.27 (a) (the owner, Oct 10: "a blinking light (slowly blinking, led, blue for scan red for he) … something that might
 * give an enemy warning that it's coming at night time"): an LED under the body that blinks slowly — blue on a scout,
 * red on a strike drone. Its core is unlit and ignores the fog, and a point of glow the same size on screen at any range
 * goes with it, so it reads at night and through weather; the terrain still hides it. It hangs off the root (not the
 * airframe), so it shows at the far tier too. It replaced the flat sight disc the view drew on the ground under it.
 *
 * Design reference: design_sheets/recon.html; the strike drone on design_sheets/weapons.html (cam=kit)
 */
import * as THREE from 'three';
import { bakeStaticParts, markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';
import type { PersonTier } from './personModel';
import { buildGrenade } from './fieldKit';

/** Drawn proportions (span 3.6) are scaled to this span, in person units: 2 × DRONE_HIT_RADIUS (0.4 u classic) × 1.8 / 1.1 ≈ 1.31, a touch over. */
const DRAWN_SPAN = 3.6;
export const DRONE_UNITS = { span: 1.4, length: 2.1 * 1.4 / DRAWN_SPAN };   // person units

export interface DroneModel {
  root: THREE.Group;        // position and heading (rotation.y) from the flight
  full: THREE.Group;
  silhouette: THREE.Group;
  airframe: THREE.Group;    // bank (z) and pitch (x) ride here
  prop: THREE.Group;        // spins about z
  lens: THREE.Object3D;     // the camera, for a scan cone or a view from the drone
  tier: PersonTier;
  /** 65.16: the strike drone's HE charge under the belly (absent on the recon drone) */
  charge?: THREE.Group;
  /** 67.27 (a): the blinking LED — its core, its glow, the clock it blinks on, and its offset in the blink (from the seed, so a swarm doesn't blink in step) */
  led: { core: THREE.Mesh; glow: THREE.Sprite; t: number; phase: number; kind: DroneLed };
}

/** 67.27 (a): what the LED says — a scanner (blue) or an explosive (red). */
export type DroneLed = 'scan' | 'he';
/** The LED's look: colours, the blink (seconds), the core's size (drawn units), the glow's size on screen (a share of its height). */
export const DRONE_LED = { scan: '#4aa8ff', he: '#ff3a2a', period: 1.6, on: 0.22, core: 0.07, glow: 0.016, dim: 0.18 };

/** 65.16: what a drone carries. Absent = the recon drone. 67.27 (a): `led` overrides the LED's kind (default: red on a strike drone, blue otherwise); `seed` offsets its blink. */
export interface DroneKit { strike?: boolean; led?: DroneLed; seed?: number }

/** 65.16: the strike drone's dive — nose down past 60°, wings level, the prop at full. The host eases into it from level flight. */
export const STRIKE_DIVE: DroneAttitude = { bank: 0, pitch: -1.15, propSpeed: 70 };

export interface DroneAttitude {
  bank: number;        // radians, positive = right wing down
  pitch: number;       // radians, positive = nose up
  propSpeed: number;   // revolutions per second (0 = stopped: launched by hand, or down)
}

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}

export function buildDroneModel(p: UnitPalette, personHeight: number, kit: DroneKit = {}): DroneModel {
  const root = new THREE.Group();
  root.scale.setScalar(personHeight / 1.8);
  const full = new THREE.Group(); root.add(full);
  full.scale.setScalar(DRONE_UNITS.span / DRAWN_SPAN);
  const airframe = new THREE.Group(); full.add(airframe);

  // Fuselage: a tapered pod in the body colour, a dark belly strip
  box(airframe, 0.24, 0.26, 1.3, p.body, 0, 0, 0.25);
  box(airframe, 0.18, 0.2, 0.35, p.body, 0, 0.0, 1.05);           // nose
  box(airframe, 0.14, 0.14, 0.8, p.body, 0, 0.03, -0.75);         // tail boom
  box(airframe, 0.2, 0.05, 1.1, p.dark, 0, -0.14, 0.3);           // belly skid (it lands on this)
  // Camera turret under the nose, its lens the flash
  const turret = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 6), mat(p.dark)); turret.position.set(0, -0.15, 0.95); airframe.add(turret);
  const lens = new THREE.Object3D(); lens.position.set(0, -0.18, 1.03); airframe.add(lens);
  box(airframe, 0.07, 0.07, 0.03, p.flash, 0, -0.17, 1.04);
  // High wing on a pylon, two halves with a little dihedral, flash tips
  box(airframe, 0.1, 0.16, 0.36, p.dark, 0, 0.2, 0.35);           // pylon
  for (const side of [-1, 1]) {
    const half = new THREE.Group(); half.position.set(0, 0.3, 0.35); half.rotation.z = side * 0.07; airframe.add(half);
    box(half, DRAWN_SPAN / 2, 0.05, 0.42, p.dark, side * DRAWN_SPAN / 4, 0, 0);
    box(half, 0.2, 0.06, 0.43, p.flash, side * (DRAWN_SPAN / 2 - 0.1), 0.005, 0);
  }
  // Tail: a horizontal stabiliser and a fin, the fin with the flash
  box(airframe, 1.0, 0.04, 0.26, p.dark, 0, 0.05, -1.05);
  box(airframe, 0.04, 0.38, 0.28, p.dark, 0, 0.22, -1.05);
  box(airframe, 0.05, 0.1, 0.2, p.flash, 0, 0.36, -1.06);
  // Pusher prop behind the wing's trailing edge, on top of the boom
  const pod = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 0.24, 8), mat(p.metal));
  pod.rotation.x = Math.PI / 2; pod.position.set(0, 0.3, -0.05); airframe.add(pod);
  const prop = new THREE.Group(); prop.position.set(0, 0.3, -0.2); airframe.add(prop);
  box(prop, 0.75, 0.06, 0.02, p.metal, 0, 0, 0);
  box(prop, 0.06, 0.06, 0.05, p.metal, 0, 0, 0);

  // 65.16: the strike drone's charge — the HE grenade at its own size (the airframe is drawn larger than person units,
  // so it is scaled back), lying along the belly with its fuse to the nose, under a dark release clip; armed: no pin
  let charge: THREE.Group | undefined;
  if (kit.strike) {
    charge = new THREE.Group(); charge.position.set(0, -0.2, 0.42); airframe.add(charge);
    box(charge, 0.12, 0.05, 0.3, p.dark, 0, 0, 0);                    // the clip, hung from the skid
    for (const z of [-0.08, 0.08]) box(charge, 0.24, 0.025, 0.035, p.dark, 0, -0.09, z);   // the two straps round the charge
    const he = buildGrenade('he', p);
    he.group.scale.setScalar(DRAWN_SPAN / DRONE_UNITS.span);          // back to person units inside the drawn airframe
    he.group.rotation.x = Math.PI / 2;                                 // lying along the belly, the fuse forward
    he.group.position.set(0, -0.13, 0);
    he.ring.parent?.remove(he.ring);                                   // armed: the pin is out
    charge.add(he.group);
  }

  // 67.27 (a): the LED, under the body's middle, off the root (so the far tier shows it too); the glow a point the same
  // size on screen at any range, both unlit and fog-free, additive so they read at night and through weather
  const kind: DroneLed = kit.led ?? (kit.strike ? 'he' : 'scan');
  const color = DRONE_LED[kind];
  const ledAt = new THREE.Vector3(0, -0.22, 0.3).multiplyScalar(DRONE_UNITS.span / DRAWN_SPAN);
  const core = new THREE.Mesh(new THREE.SphereGeometry(DRONE_LED.core * DRONE_UNITS.span / DRAWN_SPAN, 8, 6), new THREE.MeshBasicMaterial({ color, fog: false }));
  core.position.copy(ledAt);
  root.add(core);
  const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: ledGlowTexture(), color, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false, sizeAttenuation: false }));
  glow.scale.setScalar(DRONE_LED.glow);
  glow.geometry.userData.shared = true;   // three's one sprite quad: never disposed with a drone
  glow.position.copy(ledAt);
  root.add(glow);
  const led = { core, glow, t: 0, phase: ((kit.seed ?? 0) * 0.6180339887) % 1 * DRONE_LED.period, kind };

  // Far tier: a cross — fuselage and wing — plus the fin, in body and dark
  const silhouette = new THREE.Group(); root.add(silhouette);
  silhouette.scale.setScalar(DRONE_UNITS.span / DRAWN_SPAN);
  box(silhouette, 0.26, 0.28, 2.1, p.body, 0, 0, 0.05);
  box(silhouette, DRAWN_SPAN, 0.07, 0.45, p.dark, 0, 0.3, 0.35);
  box(silhouette, 0.06, 0.4, 0.3, p.dark, 0, 0.22, -1.05);
  silhouette.visible = false;

  return { root, full, silhouette, airframe, prop, lens, tier: 'full', charge, led };
}

/** Set the attitude, turn the prop by `dt` seconds at its speed, and run the LED's blink. */
export function updateDroneModel(m: DroneModel, a: DroneAttitude, dt: number): void {
  m.airframe.rotation.set(-a.pitch, 0, -a.bank, 'YXZ');
  m.prop.rotation.z += a.propSpeed * Math.PI * 2 * dt;
  setLedBlink(m, dt);
}

/** 67.27 (a): the slow blink — a short flash every `period` seconds, a dim glow between (the core stays faintly lit). */
export function setLedBlink(m: DroneModel, dt: number): boolean {
  const led = m.led;
  led.t += dt;
  const on = ((led.t + led.phase) % DRONE_LED.period) < DRONE_LED.on;
  (led.core.material as THREE.MeshBasicMaterial).color.set(DRONE_LED[led.kind]).multiplyScalar(on ? 1 : DRONE_LED.dim);
  led.glow.visible = on;
  return on;
}

let glowTex: THREE.Texture | null = null;
/** A soft round glow (white; the sprite's colour tints it), made once. */
function ledGlowTexture(): THREE.Texture {
  if (glowTex) return glowTex;
  const n = 32, data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    const dx = (x + 0.5) / n - 0.5, dy = (y + 0.5) / n - 0.5, r = Math.min(1, Math.sqrt(dx * dx + dy * dy) * 2);
    const a = Math.pow(1 - r, 2);
    const i = (y * n + x) * 4; data[i] = data[i + 1] = data[i + 2] = 255; data[i + 3] = Math.round(a * 255);
  }
  glowTex = new THREE.DataTexture(data, n, n);
  glowTex.needsUpdate = true;
  return glowTex;
}

/** Fold static parts: the airframe (minus the prop), the prop, the silhouette. */
export function bakeDroneModel(m: DroneModel): number {
  let n = bakeStaticParts(m.airframe, { exclude: [m.prop] }).partsBaked;
  n += bakeStaticParts(m.prop).partsBaked;
  m.silhouette.visible = true;
  n += bakeStaticParts(m.silhouette).partsBaked;
  setDroneTier(m, m.tier);
  return n;
}

export function setDroneTier(m: DroneModel, tier: PersonTier): void {
  m.tier = tier;
  m.full.visible = tier === 'full';
  m.silhouette.visible = tier === 'silhouette';
}
